import { useMemo, useState } from 'react';
import { useExtensionsStore } from '@/stores/extensions';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import type { AutomationItem } from '@hygge/shared';
import { summarizeRule } from '@hygge/shared';
import {
  useAutomations,
  useCreateAutomation,
  useUpdateAutomation,
  useDeleteAutomation,
} from '@/hooks/use-automations';
import { useStatuses, useProjectTags } from '@/hooks/use-tasks';
import { useAssignableMembers } from '@/hooks/use-boards';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { AutomationRecipe } from '@/lib/automations';
import { RecipeGallery } from './recipe-gallery';
import { RuleBuilder } from './rule-builder';
import {
  Zap,
  MoreHorizontal,
  Copy,
  Trash2,
  Pencil,
  GitBranch,
  ClipboardList,
  Sparkles,
} from 'lucide-react';

// Mutations must never fail silently (403 from the permissions guard, network
// errors) — surface the API message when it has one.
function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: unknown } } };
  const raw = e?.response?.data?.message;
  if (typeof raw === 'string' && raw) return raw;
  if (Array.isArray(raw) && typeof raw[0] === 'string' && raw[0]) return raw[0];
  return fallback;
}

const MAX_NAME_LENGTH = 120; // mirrors the backend DTO's MaxLength(120)

// ─── State ───────────────────────────────────────────────────────────────────

type EditingState = AutomationItem | AutomationRecipe | 'new' | null;
type EditingView = 'edit' | 'flow';

// ─── Component ───────────────────────────────────────────────────────────────

export function AutomationsTab({ boardId }: { boardId: string }) {
  const { data: rules = [], isLoading } = useAutomations(boardId);
  const update = useUpdateAutomation(boardId);
  const del = useDeleteAutomation(boardId);
  const create = useCreateAutomation(boardId);

  const [editing, setEditing] = useState<EditingState>(null);
  const [editingView, setEditingView] = useState<EditingView>('edit');
  const [showRecipes, setShowRecipes] = useState(false);
  // "Create with AI" opens the same builder as "New automation" — its only
  // point is the prompt bar at the top of it, which the assistant extension
  // governs. With AI off it is a second button that does the same thing.
  const isAssistantEnabled = useExtensionsStore((s) => s.isExtensionEnabled('ai-assistant'));
  const [deleteTarget, setDeleteTarget] = useState<AutomationItem | null>(null);

  // ─── Lookups (built once for the whole list) ──────────────────────────────

  const { data: statuses = [] } = useStatuses(boardId);
  const { data: tags = [] } = useProjectTags(boardId);
  const members = useAssignableMembers(boardId) ?? [];
  const formatName = useEmployeeNameFormatter();

  const lookups = useMemo(
    () => ({
      status: Object.fromEntries(statuses.map((s) => [s.id, s.name])),
      tag: Object.fromEntries(tags.map((t) => [t.id, t.name])),
      member: Object.fromEntries(
        members.map((m) => [
          m.employeeId,
          formatName({ firstName: m.firstName, lastName: m.lastName }),
        ])
      ),
    }),
    [statuses, tags, members, formatName]
  );

  // ─── Helpers ─────────────────────────────────────────────────────────────

  function openEdit(rule: AutomationItem) {
    setEditingView('edit');
    setEditing(rule);
  }

  function openFlow(rule: AutomationItem) {
    setEditingView('flow');
    setEditing(rule);
  }

  function handleDuplicate(r: AutomationItem) {
    create.mutate(
      {
        // Clamp: "name (copy)" can exceed the DTO's MaxLength(120) → silent 400.
        name: `${r.name} (copy)`.slice(0, MAX_NAME_LENGTH),
        // A copy must land disabled — duplicating a live rule would double every
        // side-effect (comments, notifications, assignments) until noticed.
        enabled: false,
        trigger: r.trigger,
        conditions: r.conditions,
        actions: r.actions,
      },
      {
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't duplicate the automation")),
      }
    );
  }

  function handleToggle(r: AutomationItem, enabled: boolean) {
    update.mutate(
      { id: r.id, body: { enabled } },
      {
        onError: (err) =>
          toast.error(
            apiErrorMessage(err, `Couldn't ${enabled ? 'enable' : 'disable'} the automation`)
          ),
      }
    );
  }

  function handleConfirmDelete() {
    if (!deleteTarget) return;
    del.mutate(deleteTarget.id, {
      onError: (err) => toast.error(apiErrorMessage(err, "Couldn't delete the automation")),
    });
    setDeleteTarget(null);
  }

  // ─── Sub-views ────────────────────────────────────────────────────────────

  if (showRecipes) {
    return (
      <RecipeGallery
        onPick={(recipe) => {
          setShowRecipes(false);
          setEditingView('edit');
          setEditing(recipe);
        }}
        onBack={() => setShowRecipes(false)}
      />
    );
  }

  if (editing) {
    const initial = editing === 'new' ? undefined : editing;
    return (
      <RuleBuilder
        boardId={boardId}
        initial={initial}
        onClose={() => setEditing(null)}
        initialView={editingView}
      />
    );
  }

  // ─── Header ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Automations</h3>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowRecipes(true)}>
            Browse recipes
          </Button>
          {isAssistantEnabled && (
            <Button
              size="sm"
              variant="outline"
              className="cursor-pointer focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              onClick={() => {
                setEditingView('edit');
                setEditing('new');
              }}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Create with AI
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => {
              setEditingView('edit');
              setEditing('new');
            }}
          >
            New automation
          </Button>
        </div>
      </div>

      {/* ── Loading ── */}
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {/* ── Empty state ── */}
      {!isLoading && rules.length === 0 && (
        <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-border py-12 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-hygge/10 text-hygge">
            <Zap className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">No automations yet</p>
            <p className="text-xs text-muted-foreground">
              Automations react to task events and act on your behalf.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowRecipes(true)}>
              Browse recipes
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setEditingView('edit');
                setEditing('new');
              }}
            >
              New automation
            </Button>
          </div>
        </div>
      )}

      {/* ── Rule list ── */}
      {!isLoading && rules.length > 0 && (
        <div className="space-y-2">
          {rules.map((r) => {
            const s = summarizeRule(
              { trigger: r.trigger, conditions: r.conditions, actions: r.actions },
              lookups
            );
            const ifPart = s.alwaysRuns ? '' : ` · if ${s.conditions.join(', ')}`;
            const summary = `When ${s.when.replace(/^Task /, '').toLowerCase()}${ifPart} · then ${s.actions.join(', ')}`;

            return (
              <div
                key={r.id}
                className="flex items-center justify-between gap-3 rounded-md border border-border p-3 transition-colors hover:border-hygge/30"
              >
                {/* Row body — clicking opens Flow view */}
                <button
                  type="button"
                  className="min-w-0 flex-1 cursor-pointer rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  onClick={() => openFlow(r)}
                >
                  <div className="truncate text-sm font-medium">{r.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{summary}</div>
                </button>

                {/* Controls */}
                <div className="flex items-center gap-2 shrink-0">
                  {/* Last-run pill */}
                  {r.lastRunAt ? (
                    <span className="inline-flex items-center rounded px-2 py-0.5 text-xs text-muted-foreground bg-muted">
                      Ran {formatDistanceToNow(new Date(r.lastRunAt), { addSuffix: true })}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Never run</span>
                  )}

                  {/* Enable / disable toggle */}
                  <Switch
                    checked={r.enabled}
                    onCheckedChange={(enabled) => handleToggle(r, enabled)}
                    aria-label="Enabled"
                  />

                  {/* ⋯ menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground"
                        aria-label="Rule actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-36">
                      <DropdownMenuItem onClick={() => openEdit(r)} className="cursor-pointer">
                        <Pencil className="mr-2 h-3.5 w-3.5" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openFlow(r)} className="cursor-pointer">
                        <GitBranch className="mr-2 h-3.5 w-3.5" />
                        Flow view
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDuplicate(r)}
                        className="cursor-pointer"
                      >
                        <Copy className="mr-2 h-3.5 w-3.5" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEdit(r)} className="cursor-pointer">
                        <ClipboardList className="mr-2 h-3.5 w-3.5" />
                        Run log
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setDeleteTarget(r)}
                        className="cursor-pointer text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Delete confirmation (mirrors the board-delete confirm) ── */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              The rule and its run history will be permanently deleted. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmDelete}
            >
              Delete automation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
